/*
 * Dashboard "Operaciones sin Facturar", réplica del HTML del Comité de Gerencia.
 * Conserva del proyecto: tipografía, Select/Checkbox/Input del codekit y márgenes
 * horizontales centrados. El resto replica el HTML original.
 */
import { useEffect, useMemo, useState } from "react";
import { Box, Checkbox, Description, Select, Title, shootToast } from "codekit";

import Funnelmd from "./Funnelmd";
import Tabclien from "./Tabclien";
import Tabdetal from "./Tabdetal";
import Tabimpor from "./Tabimpor";
import Tabresum from "./Tabresum";
import Tabtrend from "./Tabtrend";
import Toolbarx from "./Toolbarx";
import useCfrm from "../Confirmx/useCfrm";
import { carteraDataset } from "./utils/dataset";
import { carteraMeta, fmtCOP, groupSum, htmlTheme, prepareRows } from "./utils/carthelp";
import { readSabana } from "./utils/readxlsx";
import { readSnapshots, saveSnapshot } from "./utils/snapstor";

const TABS = [
  { id: "resumen", label: "📊 Resumen" },
  { id: "importaciones", label: "🚦 Importaciones — Semáforo" },
  { id: "clientes", label: "👥 Clientes y Directores" },
  { id: "detalle", label: "📋 Detalle" },
];

const TAB_TREND = { id: "tendencia", label: "📈 Tendencia" };

/**
 * Etiqueta del snapshot con la fecha local (toISOString adelanta el día en la tarde)
 * @returns {string} Fecha en formato YYYY-MM-DD
 */
const todayLabel = () => {
  const dHoy = new Date();
  const sMes = String(dHoy.getMonth() + 1).padStart(2, "0");
  return `${dHoy.getFullYear()}-${sMes}-${String(dHoy.getDate()).padStart(2, "0")}`;
};

/**
 * Construye las opciones de un filtro global con su placeholder
 * @param {Array} mData Datos base
 * @param {string} sKey Clave a listar
 * @param {string} sAll Texto de la opción "todos"
 * @returns {Array} Opciones [{ value, text }]
 */
const buildOptions = (mData, sKey, sAll) => {
  const mVals = [...new Set(mData.map((r) => r[sKey]))].filter(Boolean).sort();
  return [{ value: "", text: sAll }, ...mVals.map((v) => ({ value: v, text: v }))];
};

//Estilo base de las barras de opciones (réplica del .options-bar del HTML)
const optionsBarStyle = {
  display: "flex",
  gap: 18,
  flexWrap: "wrap",
  alignItems: "center",
  background: htmlTheme.card,
  border: `1px solid ${htmlTheme.border}`,
  borderRadius: 12,
  padding: "11px 16px",
  marginBottom: 14,
};

const Dashgraf = () => {
  const [sTab, setTab] = useState("resumen");
  const [sHoverTab, setHoverTab] = useState(null);
  const [bExcludeGlobal, setExcludeGlobal] = useState(true);
  const [bIncludeNegatives, setIncludeNegatives] = useState(false);
  const [sGerencia, setGerencia] = useState("");
  const [sTipo, setTipo] = useState("");
  const [mFunnel, setFunnel] = useState(null);
  const [mDataset, setDataset] = useState(carteraDataset);
  const [mLoaded, setLoaded] = useState(null);
  const [bPresenting, setPresenting] = useState(false);
  const [mSnapshots, setSnapshots] = useState(readSnapshots);
  const [sCompare, setCompare] = useState("");
  const { confirm } = useCfrm();

  //Normaliza el dataset activo (el publicado o la sábana cargada en la sesión)
  const mRaw = useMemo(() => prepareRows(mDataset), [mDataset]);

  //Base con la exclusión de GLOBAL aplicada
  const mBase = useMemo(
    () => (bExcludeGlobal ? mRaw.filter((r) => r["TIPO DE OPERACIÓN"] !== "GLOBAL") : mRaw),
    [mRaw, bExcludeGlobal],
  );

  //Aplica el toggle de valores negativos
  const mToggled = useMemo(
    () => (bIncludeNegatives ? mBase : mBase.filter((r) => r["TIPO DE OPERACIÓN"] === "GLOBAL" || r.VALOR >= 0)),
    [mBase, bIncludeNegatives],
  );

  //Aplica los filtros globales de gerencia y tipo
  const mData = useMemo(() => {
    let mList = mToggled;
    if (sGerencia) mList = mList.filter((r) => r.GERENCIA === sGerencia);
    if (sTipo) mList = mList.filter((r) => r["TIPO DE OPERACIÓN"] === sTipo);
    return mList;
  }, [mToggled, sGerencia, sTipo]);

  //Avisos de registros GLOBAL y negativos
  const mGlobalRows = useMemo(() => mRaw.filter((r) => r["TIPO DE OPERACIÓN"] === "GLOBAL"), [mRaw]);
  const nGlobalSum = mGlobalRows.reduce((a, r) => a + r.VALOR, 0);
  const mNegRows = useMemo(() => mBase.filter((r) => r.VALOR < 0), [mBase]);
  const nNegSum = mNegRows.reduce((a, r) => a + r.VALOR, 0);
  const nTotal = useMemo(() => mData.reduce((a, r) => a + r.VALOR, 0), [mData]);

  //Snapshot contra el que se compara: el elegido o el más reciente guardado
  const mSnapKeys = useMemo(() => Object.keys(mSnapshots).sort(), [mSnapshots]);
  const sActiveSnap = sCompare || mSnapKeys[mSnapKeys.length - 1] || "";
  const mCompare = mSnapshots[sActiveSnap]
    ? { label: sActiveSnap, total: mSnapshots[sActiveSnap].total }
    : null;
  const mTabs = mSnapKeys.length ? [...TABS, TAB_TREND] : TABS;

  //Sin sábana cargada ni filtros tocados, limpiar no cambiaría nada: el botón se oculta
  const bDirty = Boolean(mLoaded || sGerencia || sTipo || !bExcludeGlobal || bIncludeNegatives);

  //El fullscreen va sobre el documento (no sobre el contenedor) porque los modales
  //del codekit se portan a document.body: fuera del elemento en pantalla completa
  //el drill-down quedaría invisible.
  useEffect(() => {
    const handleFsChange = () => {
      if (!document.fullscreenElement) setPresenting(false);
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  //Abre el modal de drill-down con el nivel inicial
  const openFunnel = (mScope, mKeys, mBreadcrumb) =>
    setFunnel({ scope: mScope, keys: mKeys, breadcrumb: mBreadcrumb });

  //Limpia los filtros globales
  const clearFilters = () => {
    setGerencia("");
    setTipo("");
  };

  //Entra o sale de pantalla completa para presentar el tablero
  const handleTogglePresent = () => {
    if (bPresenting) {
      setPresenting(false);
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
      return;
    }
    setPresenting(true);
    document.documentElement.requestFullscreen?.().catch(() => {});
  };

  //Reemplaza el dataset publicado por la sábana del mes, solo para esta sesión
  const handleLoadFile = async (mFile) => {
    try {
      const { rows, sheetName } = await readSabana(mFile);
      const nValidas = prepareRows(rows).length;
      if (!nValidas) throw new Error(`La hoja "${sheetName}" no trae registros con DO.`);
      setDataset(rows);
      setLoaded({ at: new Date().toLocaleString("es-CO"), sheetName });
      clearFilters();
      const sText = `Sábana cargada desde la hoja "${sheetName}": ${nValidas.toLocaleString("es-CO")} DO.`;
      shootToast({ variant: "success", text: sText, duration: 5000, autoHide: true });
    } catch (mError) {
      shootToast({ variant: "error", text: mError.message, duration: 8000, autoHide: true });
    }
  };

  //Guarda el corte de hoy para poder comparar en próximas cargas
  const handleSaveSnapshot = () => {
    const sLabel = todayLabel();
    const mCorte = {
      total: nTotal,
      count: mData.length,
      byGer: groupSum(mData, "GERENCIA"),
      savedAt: new Date().toISOString(),
    };
    try {
      setSnapshots(saveSnapshot(sLabel, mCorte));
      //Sin selección previa, se compara contra el corte anterior y no contra el recién guardado
      if (!sCompare) setCompare(mSnapKeys[mSnapKeys.length - 1] || sLabel);
      //El corte guarda lo que se ve: con filtros activos no es comparable contra un corte completo
      const sFiltros = sGerencia || sTipo ? " con los filtros activos" : "";
      const sText = `Snapshot guardado como "${sLabel}"${sFiltros}.`;
      shootToast({ variant: "success", text: sText, duration: 5000, autoHide: true });
    } catch (mError) {
      shootToast({
        variant: "error",
        text: "No se pudo guardar el snapshot en este navegador.",
        duration: 8000,
        autoHide: true,
      });
    }
  };

  //Descarta la sábana cargada y deja el tablero como recién abierto. Solo se
  //confirma cuando hay una sábana en sesión, que es lo único que se pierde.
  const handleReset = async () => {
    if (mLoaded) {
      const bConfirmed = await confirm({
        title: "¿Limpiar el tablero?",
        body: "Se descarta la sábana cargada en esta sesión y vuelve la publicada. El histórico de snapshots no se borra.",
      });
      if (!bConfirmed) return;
    }
    const sText = mLoaded
      ? "Tablero limpio: vuelve la sábana publicada."
      : "Filtros y opciones restablecidos.";
    setDataset(carteraDataset);
    setLoaded(null);
    setExcludeGlobal(true);
    setIncludeNegatives(false);
    clearFilters();
    setTab("resumen");
    shootToast({ variant: "success", text: sText, duration: 4000, autoHide: true });
  };

  const sOrigen = mLoaded
    ? `Cargado: ${mLoaded.at} · hoja "${mLoaded.sheetName}"`
    : `Publicado: ${carteraMeta.publishedAt} · hoja "${carteraMeta.sheetName}"`;

  return (
    <Box spacing={4}>
      <div
        style={{
          maxWidth: 1260,
          margin: "0 auto",
          ...(bPresenting ? { background: htmlTheme.bg, padding: "16px 20px" } : {}),
        }}
      >
        <div
          style={{
            background: `linear-gradient(120deg, ${htmlTheme.navy} 0%, ${htmlTheme.navy2} 55%, ${htmlTheme.navyLight} 100%)`,
            color: "#fff",
            padding: "20px 32px",
            borderRadius: "12px 12px 0 0",
            boxShadow: "0 3px 14px rgba(18,36,89,.25)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 14,
          }}
        >
          <div>
            <Title variant="title-1">Operaciones sin Facturar</Title>
            <div style={{ opacity: 0.85, marginTop: 3 }}>
              <Description variant="description-3">
                {sOrigen} · {mData.length.toLocaleString("es-CO")} DO
              </Description>
            </div>
          </div>
          <Toolbarx
            onLoadFile={handleLoadFile}
            onSaveSnapshot={handleSaveSnapshot}
            onReset={handleReset}
            onTogglePresent={handleTogglePresent}
            isPresenting={bPresenting}
            canReset={bDirty}
          />
        </div>

        <div style={{ display: "flex", gap: 4, background: htmlTheme.navy, padding: "0 28px", overflowX: "auto", borderRadius: "0 0 12px 12px", marginBottom: 22 }}>
          {mTabs.map((mT) => (
            <button
              key={mT.id}
              onClick={() => setTab(mT.id)}
              onMouseEnter={() => setHoverTab(mT.id)}
              onMouseLeave={() => setHoverTab(null)}
              style={{
                background: "transparent",
                border: "none",
                color: sTab === mT.id || sHoverTab === mT.id ? "#fff" : "rgba(255,255,255,.65)",
                padding: "13px 18px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                borderBottom: `3px solid ${sTab === mT.id ? htmlTheme.coral : "transparent"}`,
                whiteSpace: "nowrap",
                transition: "color .15s ease",
              }}
            >
              {mT.label}
            </button>
          ))}
        </div>

        {!bPresenting && (
          <>
            <div style={optionsBarStyle}>
              <Checkbox checked={bExcludeGlobal} onChange={(e) => setExcludeGlobal(e.target.checked)}>
                Excluir ajustes "GLOBAL"
              </Checkbox>
              <Checkbox checked={bIncludeNegatives} onChange={(e) => setIncludeNegatives(e.target.checked)}>
                Incluir valores negativos (notas crédito) en los totales
              </Checkbox>
              {mNegRows.length ? (
                <span style={{ color: htmlTheme.muted }}>
                  <Description variant="description-3">
                    {mNegRows.length} registros negativos ({fmtCOP(nNegSum)}) — hoy {bIncludeNegatives ? "incluidos" : "excluidos"} del total
                  </Description>
                </span>
              ) : null}
            </div>

            <div style={{ ...optionsBarStyle, gap: 10 }}>
              <span style={{ fontWeight: 700, color: htmlTheme.navy2 }}>
                <Description variant="description-3">Filtrar todo el dashboard por:</Description>
              </span>
              <div style={{ minWidth: 200 }}>
                <Select options={buildOptions(mToggled, "GERENCIA", "Todas las gerencias")} value={sGerencia} onChangeValue={({ option }) => setGerencia(option.value)} />
              </div>
              <div style={{ minWidth: 230 }}>
                <Select options={buildOptions(mToggled, "TIPO DE OPERACIÓN", "Todos los tipos de operación")} value={sTipo} onChangeValue={({ option }) => setTipo(option.value)} />
              </div>
              <button
                onClick={clearFilters}
                style={{ background: "#F0F2F9", border: "none", borderRadius: 7, padding: "7px 12px", fontSize: 12.5, cursor: "pointer", color: htmlTheme.navy2, fontWeight: 600 }}
              >
                ✕ Limpiar filtros
              </button>
              <span style={{ color: htmlTheme.muted }}>
                <Description variant="description-3">El año se filtra con las tarjetas de abajo (Resumen), no aquí</Description>
              </span>
            </div>
          </>
        )}

        {mGlobalRows.length ? (
          <div style={{ background: "#FFF4E5", color: "#B45309", border: "1px solid #FBD9A5", borderRadius: 10, padding: "11px 15px", marginBottom: 14 }}>
            <Description variant="description-3">
              ⚠️ Los registros tipo GLOBAL ({mGlobalRows.length}) suman {fmtCOP(nGlobalSum)} y pueden distorsionar el neto — revísalos con el equipo antes de reportar.
            </Description>
          </div>
        ) : null}

        {sTab === "resumen" && <Tabresum data={mData} onFunnel={openFunnel} compare={mCompare} />}
        {sTab === "importaciones" && <Tabimpor data={mData} onFunnel={openFunnel} />}
        {sTab === "clientes" && <Tabclien data={mData} onFunnel={openFunnel} />}
        {sTab === "detalle" && <Tabdetal data={mData} />}
        {sTab === "tendencia" && (
          <Tabtrend snapshots={mSnapshots} compare={sActiveSnap} onCompare={setCompare} total={nTotal} />
        )}

        {mFunnel ? <Funnelmd initial={mFunnel} onClose={() => setFunnel(null)} /> : null}
      </div>
    </Box>
  );
};

export default Dashgraf;
